import React, {memo, useContext, useEffect, useMemo, useRef} from 'react';
import { Calendar, SlotInfo } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { WashEvent } from "../../types";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import { DentobookApplicationContext } from "../../context/DentobookApplicationContext";
import GetDisplayName from  "../calendar/GetDisplayName";

interface MemoizedCalendarProps {
    events: WashEvent[];
    onEventResize: (args: EventInteractionArgs<object>) => void;
    onEventDrop: (args: EventInteractionArgs<object>) => void;
    onSelectSlot: (slotInfo: SlotInfo) => void;
    onSelectEvent: (event: WashEvent | object, e: React.SyntheticEvent) => void;
    eventPropGetter: (event: any) => { style: React.CSSProperties };
    localizer: any;
    date: Date | string;
    resizable: boolean;
    components: CustomComponents;
    view: boolean;
}

interface CustomComponents {
    event?: React.ComponentType<any>;
}

export const CustomEvent = (props: any) => {
    const { locale } = useContext(DentobookApplicationContext);
    const { title, notes, start, end } = props.event;
    const {medicalStaff} = props.event;
    const {patientData} = props.event;


    const doctorPrefix = locale === 'BG' ? 'Д-р ' : 'Dr. ';
    const formattedStart = dayjs(start).format('HH:mm');
    const formattedEnd = dayjs(end).format('HH:mm');
    const tooltipLabels = useMemo(() => ({
        notes: locale === 'BG' ? 'Бележки' : 'Notes',
        doctor: locale === 'BG' ? 'Доктор' : 'Doctor',
        patient: locale === 'BG' ? 'Пациент' : 'Patient',
        from: locale === 'BG' ? 'От' : 'Start time',
        to: locale === 'BG' ? 'До' : 'End time',
    }), [locale]);

    const patientFirstName = patientData?.firstName || '';
    const patientLastName = patientData?.lastName || '';
    const doctorFirstName = medicalStaff?.firstName || '';
    const doctorLastName = medicalStaff?.lastName || '';

    const fallbackPatientFullName = title.split('\n')[0] || '';
    const fallbackDoctorFullName = title.split('\n')[1] || '';

    const [fallbackPatientFirstName, fallbackPatientLastName] = fallbackPatientFullName.split(' ').filter(Boolean);
    const [fallbackDoctorFirstName, fallbackDoctorLastName] = fallbackDoctorFullName.split(' ').filter(Boolean);

    const patientName = (
        <GetDisplayName
            firstName={patientFirstName || fallbackPatientFirstName || undefined}
            lastName={patientLastName || fallbackPatientLastName || undefined}
        />
    );

    const doctorName = (
        <GetDisplayName
            firstName={doctorFirstName || fallbackDoctorFirstName || undefined}
            lastName={doctorLastName || fallbackDoctorLastName || undefined}
        />
    );
    const tooltipContent = useMemo(() => (
        <div className="tooltip-content">
            <p><strong>{tooltipLabels.doctor}:</strong> {doctorPrefix}{doctorName}</p>
            <p><strong>{tooltipLabels.patient}:</strong> {patientName}</p>
            <p><strong>{tooltipLabels.from}:</strong> {formattedStart}</p>
            <p><strong>{tooltipLabels.to}:</strong> {formattedEnd}</p>
            {notes && <p><strong>{tooltipLabels.notes}:</strong> {notes}</p>}
        </div>
    ), [tooltipLabels, doctorName, patientName, formattedStart, formattedEnd, notes, doctorPrefix]);
    return (
        <Tooltip title={tooltipContent} placement="top" arrow>
            <div>
                <div className="dentobook-calendar-event-patient">{patientName}</div>
                <div className="dentobook-calendar-event-doctor">{doctorPrefix}{doctorName}</div>
            </div>
        </Tooltip>
    );
};


const DnDCalendar = withDragAndDrop(Calendar);

const MemoizedCalendar: React.FC<MemoizedCalendarProps> = memo(({
                                                                    events,
                                                                    onEventResize,
                                                                    onEventDrop,
                                                                    onSelectSlot,
                                                                    onSelectEvent,
                                                                    eventPropGetter,
                                                                    localizer,
                                                                    date,
                                                                    components,
                                                                    view,
                                                                }) => {
    const calendarRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (calendarRef.current) {
            const now = new Date();
            const currentHour = now.getHours();

            // Find the scrollable time content
            const timeGrid = calendarRef.current.querySelector(".rbc-time-content") as HTMLDivElement;

            if (timeGrid) {
                // Estimate height per hour (Adjust based on styling)
                const hourHeight = 50; // Each hour row is approx 50px
                timeGrid.scrollTop = currentHour * hourHeight;
            }
        }
    }, []);
    const memoizedComponents = useMemo(() => ({
        event: CustomEvent,  // default; overridden by caller if components.event is provided
        ...components,
    }), [components]);

    return (
        <div ref={calendarRef} style={{ height: 600 }}>

            <DnDCalendar
                components={memoizedComponents}
                onEventResize={onEventResize}
                onSelectSlot={onSelectSlot}
                onSelectEvent={onSelectEvent}
                events={events}
                onEventDrop={onEventDrop}
                eventPropGetter={eventPropGetter}
                selectable
                date={date}
                resizable
                className="custom-calendar"
                localizer={localizer}
                defaultView="week"
                view={view ? 'day' : 'week'}
                views={['week', 'day']}
                style={{ height: '100%' }}
                min={new Date(0, 0, 0, 0, 0)}
                max={new Date(0, 0, 0, 23, 59)}
                onView={() => {}}
                onNavigate={() => {}}
                step={30}
                timeslots={2}
            />
        </div>
    );
});
export default MemoizedCalendar;
